// MDMFieldsConfig.tsx

import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Col, Row } from "react-bootstrap";
import styles from "./MDMFieldsConfig.module.css";
import {
  CLIENT_TYPES,
  DEFAULT_SELECTED_FIELD_CODES,
  groupByCategory,
  MDMField,
  ReferenceDataOption,
} from "./MDMFieldsConfig.utils";
import {
  getAllFields,
  getSelectedFields,
  getFieldCategories,
  saveSelectedFields,
} from "./MDMFieldsConfig.service";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";
import MDMConfigColumns from "./components/MDMConfigColumns/MDMConfigColumns";
import CustomFieldCreate from "./components/CustomFieldCreate/CustomFieldCreate";
import * as Icon from "react-bootstrap-icons";

/**
 * MDMFieldsConfig Component
 *
 * Provides an interface for selecting and saving client data fields based on client type.
 * Supports filtering by field category and manages add/remove actions.
 */
const MDMFieldsConfig: React.FC = () => {
  const [targetFields, setTargetFields] = useState<Record<string, MDMField[]>>(
    {}
  );

  const [fieldsMap, setFieldsMap] = useState<Record<string, MDMField[]>>({});
  const [selectedClientType, setSelectedClientType] = useState<
    "INDIVIDUAL" | "ENTITY"
  >("INDIVIDUAL");
  const [selectedFilter, setSelectedFilter] = useState<string>("");
  const [filterOptions, setFilterOptions] = useState<ReferenceDataOption[]>([]);
  const [pendingClientType, setPendingClientType] = useState<
    "INDIVIDUAL" | "ENTITY" | null
  >(null);
  const [showAlert, setShowAlert] = useState(false);
  const [fetchingFieldsData, setFetchingFieldsData] = useState<boolean>(false)
  const [allFields, setAllFields] = useState<any[]>([]);
  const [selectedFieldData, setSelectedFieldData] = useState<any[]>([]);
  const [showAddCustomFieldModal, setShowAddCustomFieldModal] = useState(false);
  // Required for old way of implementation if needed to be reverted
  // const [initialTargetFields, setInitialTargetFields] = useState<
  //   Record<string, MDMField[]>
  // >({});
  // const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const handleFieldCreated = async () => {
    const allFieldData = await getAllFields();
    setAllFields(allFieldData);
  };

  const handleClientTypeChange = (newType: "INDIVIDUAL" | "ENTITY") => {
    // if (hasUnsavedChanges) {
    //   setPendingClientType(newType);
    //   setShowAlert(true);
    // } else {
    setSelectedClientType(newType);
    // }
  };

  const handleConfirmClientTypeChange = () => {
    if (pendingClientType) {
      setSelectedClientType(pendingClientType);
      setPendingClientType(null);
      setShowAlert(false);
    }
  };

  const handleCancelClientTypeChange = () => {
    setPendingClientType(null);
    setShowAlert(false);
  };

  const handleAddField = async (field: MDMField) => {
    const updatedField = { ...field, selected: true };
    const updatedTarget = { ...targetFields };
    updatedTarget[field.category] = [
      ...(targetFields[field.category] || []),
      updatedField,
    ];

    const updatedFieldsMap = { ...fieldsMap };
    updatedFieldsMap[field.category] = (fieldsMap[field.category] || []).filter(
      (f) => f.field_code !== field.field_code
    );

    setTargetFields(updatedTarget);
    setFieldsMap(updatedFieldsMap);
    // Keeping this in case we need to revert to bulk save option later on
    // setHasUnsavedChanges(!_.isEqual(updatedTarget, initialTargetFields));

    const fieldCodes = Object.values(updatedTarget)
      .flat()
      .map((f) => f.field_code);

    await saveSelectedFields(selectedClientType, fieldCodes);
  };

  const handleRemoveField = async (field: MDMField) => {
    const updatedField = { ...field, selected: false };
    const updatedMap = { ...fieldsMap };
    updatedMap[field.category] = [
      ...(fieldsMap[field.category] || []),
      updatedField,
    ];

    const updatedTarget = { ...targetFields };
    updatedTarget[field.category] = (targetFields[field.category] || []).filter(
      (f) => f.field_code !== field.field_code
    );

    setFieldsMap(updatedMap);
    setTargetFields(updatedTarget);
    // Keeping this in case we need to revert to bulk save option later on
    // setHasUnsavedChanges(!_.isEqual(updatedTarget, initialTargetFields));
    const fieldCodes: string[] = Object.values(updatedTarget)
      .flat()
      .map((f) => f.field_code);

    await saveSelectedFields(selectedClientType, fieldCodes);
  };

  useEffect(() => {
    const init = async () => {
      setFetchingFieldsData(true)
      const allFieldData = await getAllFields();
      const selectedFieldData = await getSelectedFields();
      const categoryOptions = await getFieldCategories();

      setAllFields(allFieldData);
      setSelectedFieldData(selectedFieldData);
      setFilterOptions(
        categoryOptions.map((val) => ({
          label: val.ReferenceDataValue,
          value: val.ReferenceDataValue,
        }))
      );
      setFetchingFieldsData(false)
    };

    init();
  }, []);

  useEffect(() => {
    if (allFields.length === 0) return;

    const entry = selectedFieldData.find(
      (record) => record.ClientType?.toUpperCase() === selectedClientType
    );

    const codes = entry
      ? entry.codes
      : DEFAULT_SELECTED_FIELD_CODES[selectedClientType];

    const selectedList = allFields.filter((f) => codes.includes(f.field_code));
    const availableList = allFields.filter((f) => !codes.includes(f.field_code));

    const groupedTargets = groupByCategory(
      selectedList.map((f) => ({ ...f, selected: true }))
    );
    const groupedSources = groupByCategory(
      availableList.map((f) => ({ ...f, selected: false }))
    );

    setTargetFields(groupedTargets);
    // setInitialTargetFields(groupedTargets);
    setFieldsMap(groupedSources);
    setSelectedFilter((prev) => prev || Object.keys(groupedSources)[0]);
  }, [selectedClientType, allFields, selectedFieldData]);

  return (
    <div className={styles.wrapper}>
      <AlertModal
        show={showAlert}
        onHide={handleCancelClientTypeChange}
        title="Unsaved Changes"
        message="You have unsaved changes. Switching client types will discard them. Do you want to continue?"
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: handleCancelClientTypeChange },
          { label: "Continue", onClick: handleConfirmClientTypeChange },
        ]}
      />

      {showAddCustomFieldModal && (
        <CustomFieldCreate
          show={showAddCustomFieldModal}
          onHide={() => setShowAddCustomFieldModal(false)}
          category={selectedFilter}
          onFieldCreated={handleFieldCreated}
        />
      )}

      <Row className={`no-gutter ${styles.pageHeaderWrapper}`}>
        <Col md={6}>
          <p className={"display-6"}>Client Data Management</p>
        </Col>
      </Row>

      <Row className={`no-gutter ${styles.columnWrapper}`}>
        <Col md={6} style={{ position: "relative" }}>
          <div className={styles.dropdownWrapper}>
            <div className={styles.dropdownSection}>
              <label className={styles.dropdownLabel}>Category</label>
              <Select
                options={filterOptions}
                value={filterOptions.find(
                  (opt) => opt.value === selectedFilter
                )}
                onChange={(opt) => setSelectedFilter(opt?.value || "")}
                classNamePrefix="react-select"
                className="custom-select"
                menuPosition="fixed"
              />
            </div>
            <div
              className={styles.createCustomFieldButton}
              onClick={() => setShowAddCustomFieldModal(true)}
            >
              <Icon.PlusCircleFill className={styles.addFieldIcon} />
              Create Custom Field
            </div>
          </div>
          <MDMConfigColumns
            columnId="source-column"
            columnTitle="All Available Fields"
            fields={fieldsMap}
            isFieldsDataFetched={!fetchingFieldsData}
            activeCategory={selectedFilter}
            onAddField={handleAddField}
          />
        </Col>
        <Col md={6} style={{ position: "relative" }}>
          <div className={styles.dropdownWrapper}>
            <div className={styles.dropdownSection}>
              <label className={styles.dropdownLabel}>Client Type</label>
              <Select
                options={CLIENT_TYPES}
                value={CLIENT_TYPES.find(
                  (opt) => opt.value === selectedClientType
                )}
                onChange={(opt) =>
                  handleClientTypeChange(opt?.value || "INDIVIDUAL")
                }
                classNamePrefix="react-select"
                className="custom-select"
                menuPosition="fixed"
              />
            </div>
          </div>
          <MDMConfigColumns
            columnId="target-column"
            columnTitle="Selected Fields"
            fields={targetFields}
            activeCategory={selectedFilter}
            onRemoveField={handleRemoveField}
          />
        </Col>
      </Row>
    </div>
  );
};

export default MDMFieldsConfig;
